/**
 * DigitalCoworkerChatSheet - Resizable chat panel for a specific Digital Coworker.
 *
 * Opens from the right as a fixed panel with a draggable divider for resizing,
 * matching the board chat pattern in BoardCanvas.
 *
 * Streams responses via POST /v1/digital-coworkers/{id}/chat.
 *
 * Reuses AssistantMessage and UserMessage from the chatV2 feature for consistent
 * message rendering (markdown, source citations, loading states).
 */

import { useEffect, useRef, useState, useCallback, KeyboardEvent } from 'react'
import { X, Send, Loader2, Bot } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
// PR 12 (gap closure): route through AssistantMessageRouter so the
// formatting_v2 flag drives the renderer choice end-to-end.
import { AssistantMessageRouter as AssistantMessage } from '@/features/chatV2/components/messages/AssistantMessageRouter'
import { UserMessage } from '@/features/chatV2/components/messages/UserMessage'
import type { DigitalCoworkerRecord } from '@/service/api'
import { useDigitalCoworkerChat } from './useDigitalCoworkerChat'
import { useTranslation } from '@/i18n'
import { coworkerAvatarUrl } from '@/utils/coworkerAvatar'

interface DigitalCoworkerChatSheetProps {
  coworker: DigitalCoworkerRecord | null
  isOpen: boolean
  onClose: () => void
}

const INITIAL_CHAT_WIDTH_PX = 480
const MIN_CHAT_WIDTH_PX = 360
const MAX_CHAT_WIDTH_PX = 900

export function DigitalCoworkerChatSheet({
  coworker,
  isOpen,
  onClose,
}: DigitalCoworkerChatSheetProps) {
  const { t } = useTranslation()
  const { messages, isLoading, input, setInput, sendMessage, clearMessages } =
    useDigitalCoworkerChat(coworker?.id ?? '')

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const isResizingRef = useRef(false)
  const [chatWidth, setChatWidth] = useState(INITIAL_CHAT_WIDTH_PX)
  const avatarUrl = coworkerAvatarUrl(coworker)
  const prevCoworkerIdRef = useRef(coworker?.id)
  const wasOpenRef = useRef(isOpen)

  // Clear chat history when opening panel or switching coworker
  const justOpened = isOpen && !wasOpenRef.current
  const coworkerChanged = isOpen && coworker?.id !== prevCoworkerIdRef.current
  wasOpenRef.current = isOpen
  prevCoworkerIdRef.current = coworker?.id
  if (justOpened || coworkerChanged) {
    clearMessages()
  }

  // Focus textarea when panel opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => textareaRef.current?.focus(), 100)
    }
  }, [isOpen])

  // Auto-scroll to the latest message
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // Resize handlers (same pattern as BoardCanvas)
  const handleMouseDown = useCallback(() => {
    isResizingRef.current = true
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
  }, [])

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizingRef.current) return
      const newWidth = Math.min(MAX_CHAT_WIDTH_PX, Math.max(MIN_CHAT_WIDTH_PX, window.innerWidth - e.clientX))
      setChatWidth(newWidth)
    }

    const stopResizing = () => {
      if (!isResizingRef.current) return
      isResizingRef.current = false
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', stopResizing)
    // Clean up resize state when mouse leaves the browser window
    window.addEventListener('blur', stopResizing)

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', stopResizing)
      window.removeEventListener('blur', stopResizing)
    }
  }, [])

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      void sendMessage()
    }
  }

  if (!isOpen || !coworker) return null

  const initials = coworker.name
    .split(' ')
    .filter(w => w.length > 0)
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-label={t('digital_coworker:chatSheet.dialogLabel', { name: coworker.name, defaultValue: 'Chat with {{name}}' })}
      style={{ width: chatWidth }}
      className="fixed top-0 right-0 h-full z-40 flex animate-in slide-in-from-right duration-300"
    >
      {/* Draggable divider */}
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label={t('digital_coworker:chatSheet.resizeLabel', { defaultValue: 'Resize chat panel' })}
        tabIndex={0}
        onMouseDown={handleMouseDown}
        className="w-1 h-full cursor-col-resize bg-gray-300 dark:bg-gray-600 hover:bg-orange-500 dark:hover:bg-orange-500 transition-colors flex-shrink-0"
      />

      {/* Chat panel */}
      <div className="flex-1 flex flex-col bg-background border-l shadow-xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center gap-3 border-b px-4 py-3 shrink-0">
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt={coworker.name}
              className="h-9 w-9 rounded-full object-cover"
            />
          ) : (
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary text-primary-foreground text-sm font-semibold shrink-0">
              {initials}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <p className="truncate font-semibold text-sm leading-tight">{coworker.name}</p>
            <p className="truncate text-xs text-muted-foreground">{coworker.job_title}</p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0"
            onClick={onClose}
            aria-label={t('digital_coworker:chatSheet.closeLabel', { defaultValue: 'Close chat' })}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Message list */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          {messages.length === 0 && (
            <div className="flex flex-col items-center justify-center h-full gap-3 text-center text-muted-foreground">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                <Bot className="h-6 w-6" />
              </div>
              <div>
                <p className="text-sm font-medium">{t('digital_coworker:chatSheet.emptyTitle', { name: coworker.name, defaultValue: 'Chat with {{name}}' })}</p>
                <p className="text-xs mt-1">
                  {t('digital_coworker:chatSheet.emptyDescription', { name: coworker.name, defaultValue: "Ask anything about {{name}}'s work, expertise, or recent activity." })}
                </p>
              </div>
            </div>
          )}

          {messages.filter((m) => m.role !== 'system').map((message, index) => {
            if (message.role === 'user') {
              return (
                <UserMessage
                  key={message.id}
                  message={message}
                  canEdit={false}
                />
              )
            }

            const messageWithoutSources = {
              ...message,
              parts: message.parts.filter((p) => p.type !== 'source'),
            }
            return (
              <AssistantMessage
                key={message.id}
                message={messageWithoutSources}
                isStreaming={message.status === 'streaming'}
                isLastMessage={index === messages.length - 1}
                agentNameOverride={coworker.name}
                agentAvatarUrlOverride={avatarUrl ?? undefined}
              />
            )
          })}

          <div ref={messagesEndRef} />
        </div>

        {/* Input area */}
        <div className="shrink-0 border-t px-4 py-3">
          <div className="flex items-end gap-2">
            <Textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t('digital_coworker:chatSheet.inputPlaceholder', { name: coworker.name, defaultValue: 'Message {{name}}…' })}
              rows={1}
              className="min-h-[40px] max-h-[120px] resize-none flex-1 text-sm"
              disabled={isLoading}
            />
            <Button
              size="icon"
              className="h-10 w-10 shrink-0"
              disabled={!input.trim() || isLoading}
              onClick={() => void sendMessage()}
              aria-label={t('digital_coworker:chatSheet.sendLabel', { defaultValue: 'Send message' })}
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
          </div>
          <p className="mt-1.5 text-[11px] text-muted-foreground">
            {t('digital_coworker:chatSheet.sendHintBefore', { defaultValue: 'Press' })}{' '}
            <kbd className="font-mono">{t('digital_coworker:chatSheet.keyEnter', { defaultValue: 'Enter' })}</kbd>{' '}
            {t('digital_coworker:chatSheet.sendHintMiddle', { defaultValue: 'to send,' })}{' '}
            <kbd className="font-mono">{t('digital_coworker:chatSheet.keyShiftEnter', { defaultValue: 'Shift+Enter' })}</kbd>{' '}
            {t('digital_coworker:chatSheet.sendHintAfter', { defaultValue: 'for a new line.' })}
          </p>
        </div>
      </div>
    </div>
  )
}
